/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useState } from 'react';

import moment from 'moment';
import { Link } from 'react-router-dom';

import {
  Badge,
  ClickAwayListener,
  IconButton,
  Box,
  Stack,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Grow,
  Alert,
  AlertTitle,
} from '@mui/material';
import { io, Socket } from 'socket.io-client';

import {
  NotificationItem,
  NotificationItemV2,
} from '../../../types/notifications';
import {
  NotificationV2,
  useGetAllNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAllNotificationsAsRead,
  useNotificationsQuery,
} from '../../../queries/notifications';

// Icons
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import acceptedIcon from '../../../assets/notifications/success.svg';
import rejectedIcon from '../../../assets/notifications/rejected.svg';
import outstandingIcon from '../../../assets/notifications/outstanding.svg';
import renewedIcon from '../../../assets/notifications/renewed.svg';
import notRenewedIcon from '../../../assets/notifications/not-renewed.svg';
import internetOnIcon from '../../../assets/notifications/internet-on.svg';
import internetOffIcon from '../../../assets/notifications/internet-off.svg';
import reminderIcon from '../../../assets/notifications/reminder.svg';
import notificationsFlag from '../../../assets/notifications-flag.svg';
import { useEffect } from 'react';
import { localstorageKeys } from '../../../services/local-storage-keys';
import { companyDataState } from '../../../store/company-data';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState } from 'store/auth';
import { notificationsState } from 'store/notifications';
import { NotificationEvents } from 'constants/notification';

const getIcon = {
  [NotificationEvents.LEGACY_BANK_APPROVED]: acceptedIcon,
  [NotificationEvents.LEGACY_BANK_REJECTED]: rejectedIcon,
  [NotificationEvents.LEGACY_OUTSTANDING_CRONJOBS]: outstandingIcon,
  [NotificationEvents.LEGACY_PAYMENT_SUCCESS]: renewedIcon,
  [NotificationEvents.LEGACY_PAYMENT_FAILURE]: notRenewedIcon,
  [NotificationEvents.LEGACY_SERVICE_ENABLED]: internetOnIcon,
  [NotificationEvents.LEGACY_INCOMING_PAYMENT]: reminderIcon,

  [NotificationEvents.LINE_DOWN]: internetOffIcon,
  [NotificationEvents.LINE_DOWN_QUOTA]: internetOffIcon,
  [NotificationEvents.LINE_UP]: internetOnIcon,
  [NotificationEvents.DOOR_DOWN]: internetOffIcon,
  [NotificationEvents.DOOR_UP]: internetOnIcon,
};
const getRoute = (event: string) => {
  switch (event) {
    case NotificationEvents.LINE_DOWN:
    case NotificationEvents.LINE_DOWN_QUOTA:
    case NotificationEvents.LINE_UP:
      return '/management/lines-monitoring';
    case NotificationEvents.DOOR_DOWN:
    case NotificationEvents.DOOR_UP:
      return '/door-access';
    case NotificationEvents.LEGACY_BANK_APPROVED:
    case NotificationEvents.LEGACY_BANK_REJECTED:
    case NotificationEvents.LEGACY_OUTSTANDING_CRONJOBS:
    case NotificationEvents.LEGACY_PAYMENT_SUCCESS:
    case NotificationEvents.LEGACY_PAYMENT_FAILURE:
      return '/billing';
    default:
      return '/';
  }
};
export const Notifications: FC = () => {
  const [open, setOpen] = useState(false);
  const { data: unreadCountData, refetch: refetchUnreadCount } =
    useGetUnreadCountQuery();
  const [extraUnread, setExtraUnread] = useState(0);
  const unreadCount = (unreadCountData?.unreadCount ?? 0) + extraUnread;
  const {
    data: notificationsData,
    isLoading,
    isError,
    error,
    refetch: refetchNotifications,
  } = useGetAllNotificationsQuery({
    page: 1,
    size: 99,
  });
  const [notifications, setNotifications] = useState<
    NotificationV2[] | undefined
  >();
  useEffect(() => {
    setNotifications(
      notificationsData?.items?.filter((item) => item.isRead === false),
    );
  }, [notificationsData]);

  const oldReadNotifications = localStorage.getItem(
    localstorageKeys.unReadNotifications,
  );
  const { isHasOutstandingPayment, subscriptionInfo } =
    useRecoilValue(companyDataState);

  const isReminder = () => {
    const today = moment();
    const nextDate = moment(subscriptionInfo?.nextPaymentDate);
    //get diff between nextpaymentdate and today
    const remainingDays = nextDate.diff(today, 'days');
    return !!(
      remainingDays <= 7 &&
      remainingDays >= 1 &&
      subscriptionInfo.total > 0
    );
  };

  const { mutate: markAllAsReadMutation } = useMarkAllNotificationsAsRead();

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const setLocalstorage = (number: number) =>
    localStorage.setItem(
      localstorageKeys.unReadNotifications,
      number.toString(),
    );

  const [notificationsDisplay, setNotificationDisplay] =
    useRecoilState(notificationsState);

  const RenderCard: FC<{ item: NotificationV2; isNotRead?: boolean }> = ({
    item,
    isNotRead,
  }) => {
    const { createdAt, title, body, channel } = item;

    return (
      <Box
        sx={[
          {
            width: '100%',
            display: 'flex',
            mb: 1,
            py: 2,
            px: 1,
            borderRadius: '4px',
            ':hover': {
              cursor: 'pointer',
              bgcolor: 'divider',
            },
            textDecoration: 'none',
          },
          isNotRead
            ? {
                bgcolor: 'secondary.light',
              }
            : {},
        ]}
        component={Link}
        to={getRoute(item.event) || ''}
      >
        <Avatar
          sx={{
            width: '58px',
            height: '58px',
            mr: 2,
            p: 1,
          }}
          src={getIcon[item.event as keyof typeof getIcon]}
        />
        <Box>
          <Box sx={{ display: 'flex' }}>
            <Typography variant='body2' fontWeight={500} color='primary'>
              {title}{' '}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              fontWeight={400}
              sx={{ mx: 1 }}
            >
              {moment(createdAt).fromNow()}
            </Typography>
          </Box>
          <Typography style={{ fontSize: '13px' }}>{body}</Typography>
        </Box>
      </Box>
    );
  };
  const accessToken = useRecoilValue(authState).token;
  const [liveNotification, setLiveNotification] =
    useState<NotificationV2 | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    const socket: Socket = io(
      `${process.env.REACT_APP_NOTIFICATION_SOCKET_URL}/realtime`,
      {
        transports: ['websocket'],
        auth: (cb) => cb({ token: accessToken }),
      },
    );

    socket.on('connect', () => console.log('[notifications:ws] connected'));
    socket.on('disconnect', (reason) =>
      console.log('[notifications:ws] disconnected', reason),
    );
    socket.on('connect_error', (err) =>
      console.error('[notifications:ws] connect_error', err.message),
    );

    socket.on('notifications', (notification: NotificationV2) => {
      setLiveNotification(notification);
      setNotifications((prev) =>
        prev ? [notification, ...prev] : [notification],
      );
      setExtraUnread((prev) => prev + 1);
      setTimeout(() => setLiveNotification(null), 5000);
    });

    return () => {
      socket.disconnect();
    };
  }, [accessToken]);
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation(void 0, {
      onSuccess: () => {
        setExtraUnread(0);
        refetchUnreadCount();
        refetchNotifications();
      },
    });
  };
  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleClick}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: 'neutral.main',
              color: 'primary.main',
              ':hover': { bgcolor: 'primary.light' },
            }}
          >
            <Badge badgeContent={unreadCount} color='error'>
              <NotificationsNoneOutlinedIcon fontSize='small' />
            </Badge>
          </IconButton>
          {open ? (
            <Box
              sx={{
                position: 'absolute',
                top: isHasOutstandingPayment || isReminder() ? 108 : 48,
                right: 0,
                zIndex: 1500,
                p: '20px',
                bgcolor: 'background.paper',
                width: '680px',
                boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.16)',
                border: 'solid 1px divider',
                maxHeight:
                  isHasOutstandingPayment || isReminder() ? '82vh' : '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                }}
              >
                <Typography variant='h6'>Notifications</Typography>

                <Button
                  color='secondary'
                  style={{ padding: '0px !important' }}
                  onClick={() => {
                    handleMarkAllAsRead();
                    // data?.notifications.length &&
                    //   setLocalstorage(data?.notifications.length);
                    // setUnReadNumber(0);
                  }}
                >
                  Mark all as read
                </Button>
              </Stack>

              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  alignItems: 'center',
                  overflow: 'auto',
                }}
              >
                {/*  */}
                {// data &&
                //   data.notifications
                notifications?.map((item) => (
                  <RenderCard
                    key={item.id}
                    item={item}
                    isNotRead={!item.isRead}
                  />
                ))}

                {isLoading && <CircularProgress sx={{ my: 2 }} size={30} />}

                {isError && (
                  <Grow in={isError}>
                    <Box mb={3}>
                      <Alert severity='error'>
                        <AlertTitle>Error: fetch notifications</AlertTitle>
                        {error?.error?.message || 'somthing went wrong'}
                      </Alert>
                    </Box>
                  </Grow>
                )}

                <Stack
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '1rem',
                  }}
                >
                  <img src={notificationsFlag} alt='notifications-flag' />
                  <Typography style={{ fontSize: '13px' }} textAlign='center'>
                    That's all your notifications
                    <br />
                    from the last 30 days.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          ) : null}
        </Box>
      </ClickAwayListener>
      {liveNotification && (
        <Grow in>
          <Box
            sx={{
              position: 'fixed',
              top: 24,
              left: '40%',
              zIndex: 2000,
              width: 360,
              bgcolor: 'background.paper',
              boxShadow: 4,
              borderRadius: 2,
              p: 2,
              display: 'flex',
              gap: 2,
            }}
          >
            <Avatar
              src={getIcon[liveNotification.event as keyof typeof getIcon]}
            />
            <Box>
              <Typography fontWeight={600} variant='body2'>
                {liveNotification.title}
              </Typography>
              <Typography fontSize='13px' color='text.secondary'>
                {liveNotification.body}
              </Typography>
            </Box>
          </Box>
        </Grow>
      )}
    </>
  );
};
